import { ref, onMounted, onUnmounted } from 'vue'

export interface PWAState {
  isInstalled: boolean
  canInstall: boolean
  isOffline: boolean
  updateAvailable: boolean
  registration: ServiceWorkerRegistration | null
}

export function usePWA() {
  const state = ref<PWAState>({
    isInstalled: false,
    canInstall: false,
    isOffline: !navigator.onLine,
    updateAvailable: false,
    registration: null,
  })

  let deferredPrompt: any = null

  // Verificar se está instalado
  const checkInstalled = () => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      state.value.isInstalled = true
    }
  }

  // Instalar PWA
  const install = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      console.log('PWA instalado com sucesso')
      state.value.isInstalled = true
    }

    deferredPrompt = null
    state.value.canInstall = false
  }

  // Atualizar Service Worker
  const update = async () => {
    if (!state.value.registration?.waiting) return

    // Enviar mensagem para o SW skip waiting
    state.value.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  }

  // Verificar atualizações
  const checkForUpdates = async () => {
    if (!state.value.registration) return

    await state.value.registration.update()
  }

  // Event listeners
  const handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault()
    deferredPrompt = e
    state.value.canInstall = true
  }

  const handleAppInstalled = () => {
    state.value.isInstalled = true
    state.value.canInstall = false
    deferredPrompt = null
  }

  const handleOnline = () => {
    state.value.isOffline = false
  }

  const handleOffline = () => {
    state.value.isOffline = true
  }

  const handleControllerChange = () => {
    window.location.reload()
  }

  onMounted(() => {
    checkInstalled()

    // Registrar eventos
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Registrar Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('SW registrado:', registration)
          state.value.registration = registration

          // Verificar se há um SW waiting
          if (registration.waiting) {
            state.value.updateAvailable = true
          }

          // Novo SW encontrado
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing
            if (!newWorker) return

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                state.value.updateAvailable = true
              }
            })
          })
        })
        .catch((error) => {
          console.error('Erro ao registrar SW:', error)
        })

      // Atualização do controller
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
    }
  })

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange)
  })

  return {
    state,
    install,
    update,
    checkForUpdates,
  }
}
